import { Formik, FormikProps } from "formik";
import { isString } from "lodash-es";
import React, { FunctionComponent, memo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { VALIDATION_TYPES } from "@/components/Formik";
import { FormikError } from "@/components/Formik";
import { ABOUT_ME_MAX_LENGTH, IAboutMe, IConsultantProfile } from "@/data/consultant-profiles";
import { Flex, Group, Text } from "@/eworkitComponents";
import { FormikForm, FormikTextarea } from "@/formikComponents";
import { Messenger } from "@/views/components/Messenger";

import { profilePartialUpdaterBuilder } from "../profilePartialUpdaterBuilder";
import { IProfileFormikProps } from "../types";

export const validationAboutMeFormSchema = Yup.object().shape({
  aboutMe: Yup.object().shape({
    professionalSummary: Yup.string().max(ABOUT_ME_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH).nullable(),
    reasonOfBeingProfessionalist: Yup.string().max(ABOUT_ME_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH).nullable(),
    perfectCoworkersProfile: Yup.string().max(ABOUT_ME_MAX_LENGTH, VALIDATION_TYPES.MAX_LENGTH).nullable(),
  }),
});

export interface IAboutMeFormSectionValues {
  aboutMe: IAboutMe;
}

export interface IAboutMeFormSectionProps {
  form: FormikProps<IAboutMeFormSectionValues>;
}

export const ABOUT_ME_MESSENGER_CONTEXT = "profile-about-me-form";

export const FORM_EMPTY_INITIAL_VALUES = {
  aboutMe: {
    professionalSummary: "",
    reasonOfBeingProfessionalist: "",
    perfectCoworkersProfile: "",
  },
};

interface IAboutMeFormProps extends IAboutMeFormSectionProps, IEditableSectionRenderProps {
  source: IConsultantProfile;
}

export const AboutMeFormComponent: FunctionComponent<IAboutMeFormSectionProps> = memo(({ form }) => {
  const { t } = useTranslation("session");

  return (
    <Flex direction="column" innerMargin="md">
      <Messenger context={ABOUT_ME_MESSENGER_CONTEXT} />
      <Flex direction="column">
        <Group direction="column" label={t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.PROFESSIONAL_SUMMARY_QUESTION")}>
          <FormikTextarea
            name="aboutMe.professionalSummary"
            testName="aboutMe.professionalSummary"
            value={form.values.aboutMe.professionalSummary}
            size="lg"
            resize="vertical"
          />
          <Flex justify="space-between">
            <Flex>
              <FormikError
                name="aboutMe.professionalSummary"
                data-test="error-aboutMe.professionalSummary"
                overrideMessages={{
                  MAX_LENGTH: t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.MAX_LENGTH"),
                }}
              />
            </Flex>
            <Flex align="flex-end">
              <Text size="sm" color="light">
                {`${
                  isString(form.values.aboutMe.professionalSummary) ? form.values.aboutMe.professionalSummary.length : 0
                }/${ABOUT_ME_MAX_LENGTH}`}
              </Text>
            </Flex>
          </Flex>
        </Group>
        <Group
          direction="column"
          label={t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.REASON_OF_BEING_PROFESSIONALIST_QUESTION")}>
          <FormikTextarea
            name="aboutMe.reasonOfBeingProfessionalist"
            testName="aboutMe.reasonOfBeingProfessionalist"
            value={form.values.aboutMe.reasonOfBeingProfessionalist}
            size="lg"
            resize="vertical"
          />
          <Flex justify="space-between">
            <Flex>
              <FormikError
                name="aboutMe.reasonOfBeingProfessionalist"
                data-test="error-aboutMe.reasonOfBeingProfessionalist"
                overrideMessages={{
                  MAX_LENGTH: t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.MAX_LENGTH"),
                }}
              />
            </Flex>
            <Flex align="flex-end">
              <Text size="sm" color="light">
                {`${
                  isString(form.values.aboutMe.reasonOfBeingProfessionalist)
                    ? form.values.aboutMe.reasonOfBeingProfessionalist.length
                    : 0
                }/${ABOUT_ME_MAX_LENGTH}`}
              </Text>
            </Flex>
          </Flex>
        </Group>
        <Group direction="column" label={t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.PERFECT_COWORKERS_PROFILE_QUESTION")}>
          <FormikTextarea
            name="aboutMe.perfectCoworkersProfile"
            testName="aboutMe.perfectCoworkersProfile"
            value={form.values.aboutMe.perfectCoworkersProfile}
            size="lg"
            resize="vertical"
          />
          <Flex justify="space-between">
            <Flex>
              <FormikError
                name="aboutMe.perfectCoworkersProfile"
                data-test="error-aboutMe.perfectCoworkersProfile"
                overrideMessages={{
                  MAX_LENGTH: t("CONSULTANT_PROFILES.DETAIL.ABOUT_ME.MAX_LENGTH"),
                }}
              />
            </Flex>
            <Flex align="flex-end">
              <Text size="sm" color="light">
                {`${
                  isString(form.values.aboutMe.perfectCoworkersProfile)
                    ? form.values.aboutMe.perfectCoworkersProfile.length
                    : 0
                }/${ABOUT_ME_MAX_LENGTH}`}
              </Text>
            </Flex>
          </Flex>
        </Group>
      </Flex>
    </Flex>
  );
});

export const AboutMeForm: FunctionComponent<IAboutMeFormProps> = memo(props => {
  const { form, editableProps } = props;
  useEditableSubmit(form, editableProps);

  return (
    <FormikForm>
      <AboutMeFormComponent form={form} />
    </FormikForm>
  );
});

interface IAboutMeFormikProps extends IProfileFormikProps<IAboutMeFormSectionValues>, IAboutMeFormProps {}

export const AboutMeFormik: FunctionComponent<IAboutMeFormikProps> = memo(
  ({ initialValues, validationSchema, onSubmit, ...rest }) => (
    <Formik
      initialValues={{ ...FORM_EMPTY_INITIAL_VALUES, ...initialValues }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={(form: FormikProps<IAboutMeFormSectionValues>) => <AboutMeForm {...rest} form={form} />}
    />
  ),
);

export const ConsultantProfileAboutMeEdit = profilePartialUpdaterBuilder(AboutMeFormik, {
  validationSchema: validationAboutMeFormSchema,
  messengerContext: ABOUT_ME_MESSENGER_CONTEXT,
});
